import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import { UserRole } from '../../../backend/src/services/resources/users/user.model'
import { CreateUserParams } from '../../../backend/src/services/resources/users/users.controller.service.types'
import useStore from '../store'
import { Organization } from '../api/organizations'
import {
  AvailableWorker,
  deleteAccount,
  deleteWorkerAssociation,
  discontinueUserOrganizationRole,
  discontinueWorkerAssociation,
  FetchAvailableParams,
  fetchAvailableWorkers,
  fetchWorkers,
  FetchWorkersParams,
  get,
  regenerateQrCode,
  update,
  updateAssociation,
  updateNotificationSettings,
  UpdateNotificationSettingsPayload,
  updateRole,
  UpdateUserAssociationPayload,
  updateUserEmail,
  updateUserOrganizationRole,
  UpdateUserOrganizationRolePayload,
  UpdateUserPayload,
  UpdateUserRolePayload,
  updateWorker,
  UpdateWorkerPayload,
  getWorkerQrCode,
  getWorker,
  UpdateWorkerEmployerCollaborationPayload,
  updateWorkerEmployerCollaboration,
  UpdateWorkerAutoClockingPayload,
  updateWorkerAutoClocking,
} from '../api/users'
import {
  auth,
  AuthResponse,
  completeProfile,
  CompleteUserProfilePayload,
  count,
  create,
  fetch,
  FetchUsersParams,
  getUserQrCode,
  isAuth,
  requestNewPassword,
  RequestNewPasswordPayload,
  resetPassword,
  ResetUserPasswordPayload,
  User,
  validateNewEmail,
} from '../api/users'
import axios, { AxiosError } from 'axios'

export const userKeys = {
  all:     ['users']                                              as const,
  isAuth:  ['users', 'is-auth']                                  as const,
  qr:      (id: string) => ['user', id, 'qr']                    as const,
  mutations: {
    create:                           ['users', 'create']                                  as const,
    update:                           ['users', 'update']                                  as const,
    updateWorker:                     ['users', 'workers', 'update']                       as const,
    updateEmail:                      ['users', 'update', 'email']                         as const,
    updateNotificationSettings:       ['users', 'update', 'notificaiton-settings']         as const,
    completeProfile:                  ['users', 'complete-profile']                        as const,
    requestPassword:                  ['users', 'request-password']                        as const,
    resetPassword:                    ['users', 'reset-password']                          as const,
    deleteAccount:                    ['users', 'delete']                                  as const,
    validateNewEmail:                 ['users', 'validate-new-email']                      as const,
    updateUserRole:                   ['users', 'roles', 'update']                         as const,
    updateUserAssociation:            ['users', 'associations', 'update']                  as const,
    discontinueUserOrganizationRole:  ['users', 'roles', 'discontinue']                    as const,
    updateUserOrganizationRole:       ['users', 'roles', 'update']                         as const,
    discontinueWorkerAssociation:     ['users', 'associations', 'discontinue']             as const,
    deleteWorkerAssociation:          (workerId: string) =>
                                        ['worker', 'association', 'delete', workerId]      as const,
    regenerateUserQrCode:             ['users', 'qr-regenerate']                           as const,
    updateWorkerEmployerCollaboration: ['users', 'workers', 'employer-collaboration', 'update'] as const,
    auth:                             ['users', 'auth']                                    as const,
  },
} as const

//Queries
export const useUsersQuery = (
  filters?: FetchUsersParams,
  options?: QueryOptions<any>,
): UseQueryResult<User[]> =>
  useResources<User[], FetchUsersParams>('users', fetch, filters || {}, options)

/**
 * Hook to get the count of users matching the given filters
 * @param filters - Optional filters to apply when counting users
 * @param options - Optional React Query options
 * @returns UseQueryResult containing the count of matching users
 */
export const useUsersCountQuery = (
  filters?: FetchUsersParams,
  options?: QueryOptions<any>,
): UseQueryResult<number> =>
  useResources<number, FetchUsersParams>('users/count', count, filters || {}, options)

export const useUserQuery = (id?: string): UseQueryResult<User> => useResource('users', get, id)

export const useUserQrQuery = (
  userId: string,
  options?: QueryOptions<any>,
): UseQueryResult<string> =>
  useQuery(userKeys.qr(userId), async () => getUserQrCode(userId), {
    enabled: Boolean(userId),
    ...options,
  } as any)

export const useWorkerQrQuery = (
  workerId: string,
  options?: QueryOptions<any>,
): UseQueryResult<string> =>
  useQuery(['worker', workerId, 'qr'], async () => getWorkerQrCode(workerId), {
    enabled: Boolean(workerId),
    ...options,
  } as any)

export const useWorkerQuery = (
  workerId: string,
  options?: QueryOptions<any>,
): UseQueryResult<User> => useResource('workers', getWorker, workerId, undefined, options)

export const useAvailableWorkersQuery = (
  params: FetchAvailableParams,
  options?: QueryOptions<AvailableWorker[]>,
): UseQueryResult<AvailableWorker[]> =>
  useResources<AvailableWorker[], FetchAvailableParams>(
    'workers',
    fetchAvailableWorkers,
    params,
    options,
  )

export const useWorkersQuery = (
  filters?: FetchWorkersParams,
  options?: QueryOptions<any>,
): UseQueryResult<User[]> =>
  useResources<User[], FetchWorkersParams>('workers', fetchWorkers, filters || {}, options)

const onMutationSuccess = () => {
  queryClient.invalidateQueries(userKeys.all)
  queryClient.invalidateQueries(['workers'])
  queryClient.invalidateQueries(userKeys.isAuth)
  queryClient.invalidateQueries(['app-config'])
}

export const useCreateMutation = (): UseMutationResult<User, CreateUserParams> =>
  useMutation(create, {
    mutationKey: userKeys.mutations.create,
    onSuccess: onMutationSuccess,
  })

export const useUpdateMutation = (): UseMutationResult<User, UpdateUserPayload> =>
  useMutation(update, {
    mutationKey: userKeys.mutations.update,
    onSuccess: onMutationSuccess,
  })

export const useUpdateWorkerMutation = (): UseMutationResult<User, UpdateWorkerPayload> =>
  useMutation(updateWorker, {
    mutationKey: userKeys.mutations.updateWorker,
    onSuccess: onMutationSuccess,
  })

export const useUpdateEmailMutation = (): UseMutationResult<User, string> =>
  useMutation(updateUserEmail, { mutationKey: userKeys.mutations.updateEmail })

export const useUpdateNotificationSettingsMutation = (): UseMutationResult<
  User,
  UpdateNotificationSettingsPayload
> =>
  useMutation(updateNotificationSettings, {
    mutationKey: userKeys.mutations.updateNotificationSettings,
    onSuccess: onMutationSuccess,
  })

export const useCompleteProfile = (): UseMutationResult<User, CompleteUserProfilePayload> =>
  useMutation(completeProfile, { mutationKey: userKeys.mutations.completeProfile })

export const useRequestPasswordMutation = (): UseMutationResult<
  User,
  RequestNewPasswordPayload
> => useMutation(requestNewPassword, { mutationKey: userKeys.mutations.requestPassword })

export const useResetPasswordMutation = (): UseMutationResult<
  User,
  ResetUserPasswordPayload
> => useMutation(resetPassword, { mutationKey: userKeys.mutations.resetPassword })

export const useDeleteAccountMutation = (): UseMutationResult<User, User['_id']> => {
  return useMutation(deleteAccount, { mutationKey: userKeys.mutations.deleteAccount })
}

export const useValidateNewEmailMutation = (): UseMutationResult<User, string> =>
  useMutation(validateNewEmail, {
    mutationKey: userKeys.mutations.validateNewEmail,
    onSuccess: onMutationSuccess,
  })

export const useUpdateUserRoleMutation = (): UseMutationResult<User, UpdateUserRolePayload> =>
  useMutation(updateRole, {
    mutationKey: userKeys.mutations.updateUserRole,
    onSuccess: onMutationSuccess,
  })

export const useUpdateUserAssociationMutation = (): UseMutationResult<
  User,
  UpdateUserAssociationPayload
> =>
  useMutation(updateAssociation, {
    mutationKey: userKeys.mutations.updateUserAssociation,
    onSuccess: onMutationSuccess,
  })

export const useDiscontinueUserOrganizationRoleMutation = (): UseMutationResult<
  User,
  { userId: string }
> =>
  useMutation(discontinueUserOrganizationRole, {
    mutationKey: userKeys.mutations.discontinueUserOrganizationRole,
    onSuccess: onMutationSuccess,
  })

export const useUpdateUserOrganizationRoleMutation = (): UseMutationResult<
  User,
  UpdateUserOrganizationRolePayload
> =>
  useMutation(updateUserOrganizationRole, {
    mutationKey: userKeys.mutations.updateUserOrganizationRole,
    onSuccess: onMutationSuccess,
  })

export const useDiscontinueWorkerAssociationMutation = (): UseMutationResult<
  User,
  { workerId: string }
> =>
  useMutation(discontinueWorkerAssociation, {
    mutationKey: userKeys.mutations.discontinueWorkerAssociation,
    onSuccess: onMutationSuccess,
  })

export const useDeleteWorkerAssociationMutation = (
  workerId: string,
): UseMutationResult<User, { workerId: string }> =>
  useMutation(deleteWorkerAssociation, {
    mutationKey: userKeys.mutations.deleteWorkerAssociation(workerId),
    onSuccess: onMutationSuccess,
  })

export const useRegenerateUserQrCodeMutation = (): UseMutationResult<string, string> =>
  useMutation(regenerateQrCode, {
    mutationKey: userKeys.mutations.regenerateUserQrCode,
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries(userKeys.qr(userId))
    },
  })

export const useUpdateWorkerEmployerCollaborationMutation = (): UseMutationResult<
  User,
  UpdateWorkerEmployerCollaborationPayload
> =>
  useMutation(updateWorkerEmployerCollaboration, {
    mutationKey: userKeys.mutations.updateWorkerEmployerCollaboration,
    onSuccess: () => {
      queryClient.invalidateQueries(['missions'])
      queryClient.invalidateQueries(['workers'])
      onMutationSuccess()
    },
  })

const useUpdateWorkerAutoClockingMutationKey = (): QueryKey => [
  'users',
  'workers',
  'auto-clocking',
  'update',
]
export const useUpdateWorkerAutoClockingMutation = (): UseMutationResult<
  User,
  UpdateWorkerAutoClockingPayload,
  any
> =>
  useMutation(updateWorkerAutoClocking, {
    mutationKey: useUpdateWorkerAutoClockingMutationKey(),
    onSuccess: onMutationSuccess,
  })

//Auth mutations

export const useIsAuthQuery = (): UseQueryResult<AuthResponse> =>
  useQuery<any, AxiosError, AuthResponse>(userKeys.isAuth, isAuth, {
    staleTime: 1000 * 60 * 10, // 10 minutes
    onError: onAuthFail,
    onSuccess: data => {
      // Update session data in store
      const { session } = useStore.getState()

      // Get the last version of currently stored user role in API response
      // to update "currentRole" & "currentOrganization" in store
      const currentRole =
        session.currentUserRole &&
        data.user.roles.find(
          (role: UserRole) =>
            role.kind === session.currentUserRole &&
            (role.organization as Organization | undefined)?._id ===
              session.currentOrganization?._id,
        )

      // Get new version of "currentOrganization"
      let currentOrganization = currentRole?.organization as Organization | undefined
      // When current user is Super Admin and "logged as" an organization we keep the stored "currentOrganization"
      // because the current store organization is not necessarily in the API response
      if (session.isSuperAdmin && session.loggedAs?.organization)
        currentOrganization = session.loggedAs?.organization

      // Store session data
      useStore
        .getState()
        .session.initializeSession(
          data.user,
          data.jwt,
          session.currentUserRole,
          currentOrganization,
          currentRole?.organizationRole || 'admin',
        )
    },
  })

const onAuthFail = (data: unknown) => {
  if (axios.isAxiosError(data)) {
    const error = data.toJSON() as AxiosError
    //@ts-expect-error
    if (error.status === 403 || error.status === 401) {
      // Initialize session as guest when auth fail expect the failure is a network error or outdated app version error
      useStore.getState().session.initializeSession()
    }
  }
}

export type AuthMutationResult = {
  user: User
  jwt: string
}

export const useAuthMutation = () =>
  useMutation(auth, {
    mutationKey: userKeys.mutations.auth,
    onError: onAuthFail,
    onSuccess: (authResponse) => {
      // Store session data on login success

      const activeRoles = authResponse.user?.roles.filter((role: UserRole) => role.status === 'accepted')

      // If user is Super Admin, we prioritize this role as current role
      // Because Super Admin role is not always the first role in organizations array
      const superAdminRole = activeRoles.find((role: UserRole) => role.kind === 'superAdmin')
      const firstActiveRole = activeRoles[0]
      const currentRole = superAdminRole ?? firstActiveRole

      useStore
        .getState()
        .session.initializeSession(
          authResponse.user,
          authResponse.jwt,
          currentRole?.kind,
          currentRole?.organization as Organization | undefined,
          currentRole?.organizationRole,
        )
    },
  })
